import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api";
import type { NotificationFeed } from "../api/types";

/**
 * The bell's data hook: polls GET /api/notifications and exposes optimistic
 * mark-read / mark-all actions. Deny-by-default friendly — a 401 (logged out)
 * or any transport error resolves to an empty feed with the error surfaced, so
 * the bell simply shows nothing rather than crashing the shell.
 */

const EMPTY: NotificationFeed = { unread_count: 0, notifications: [] };
const DEFAULT_POLL_MS = 60_000;

export interface UseNotifications {
  feed: NotificationFeed;
  loading: boolean;
  error: unknown;
  refresh: () => Promise<void>;
  markRead: (id: number) => Promise<void>;
  markAll: () => Promise<void>;
}

function applyRead(prev: NotificationFeed, id: number): NotificationFeed {
  let flipped = false;
  const notifications = prev.notifications.map((n) => {
    if (n.id === id && !n.read_at) {
      flipped = true;
      return { ...n, read_at: new Date().toISOString() };
    }
    return n;
  });
  return {
    unread_count: Math.max(0, prev.unread_count - (flipped ? 1 : 0)),
    notifications,
  };
}

function applyReadAll(prev: NotificationFeed): NotificationFeed {
  const now = new Date().toISOString();
  return {
    unread_count: 0,
    notifications: prev.notifications.map((n) =>
      n.read_at ? n : { ...n, read_at: now },
    ),
  };
}

export function useNotifications(
  pollMs: number = DEFAULT_POLL_MS,
): UseNotifications {
  const [feed, setFeed] = useState<NotificationFeed>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const alive = useRef(true);

  const refresh = useCallback(async () => {
    try {
      const f = await api.listNotifications();
      if (alive.current) {
        setFeed(f);
        setError(null);
      }
    } catch (e) {
      if (alive.current) {
        setError(e);
        setFeed(EMPTY);
      }
    } finally {
      if (alive.current) setLoading(false);
    }
  }, []);

  const markRead = useCallback(
    async (id: number) => {
      setFeed((prev) => applyRead(prev, id)); // optimistic
      try {
        await api.markNotificationRead(id);
      } catch {
        await refresh(); // reconcile on failure
      }
    },
    [refresh],
  );

  const markAll = useCallback(async () => {
    setFeed((prev) => applyReadAll(prev)); // optimistic
    try {
      await api.markAllNotificationsRead();
    } catch {
      await refresh();
    }
  }, [refresh]);

  useEffect(() => {
    alive.current = true;
    void refresh();
    const t = setInterval(() => void refresh(), pollMs);
    return () => {
      alive.current = false;
      clearInterval(t);
    };
  }, [refresh, pollMs]);

  return { feed, loading, error, refresh, markRead, markAll };
}
